import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import searchIcon from "@assets/facilityNetwork/lf-search.svg";
import csvChevronIcon from "@assets/facilityNetwork/ac-csv-right.svg";
import clearIcon from "@assets/facilityNetwork/ac-clear-chevron.svg";
import chipDismissIcon from "@assets/facilityNetwork/ac-chip-dismiss.svg";
import reviewChevronIcon from "@assets/facilityNetwork/ac-review-right.svg";
import cloudUploadIcon from "@assets/facilityNetwork/ba-cloud-upload.png";
import documentIcon from "@assets/facilityNetwork/ba-document.svg";
import fileDismissIcon from "@assets/facilityNetwork/ba-file-dismiss.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import {
  formatReviewChipLabel,
  formatSearchSubtitle,
  mapNotFoundNpi,
  mergeSelectedChildFacilities,
  searchChildFacilities,
  type ChildFacilitySearchHit,
} from "./addChildFacilitySearchUtils";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import {
  CHILD_NPI_CSV_TEMPLATE_FILENAME,
  formatCsvFileSize,
  isCsvUploadFile,
  parsePastedCcns,
} from "./bulkAddCsvUtils";
import { downloadNpiCsv } from "@services/facilityNetworkLink";
import { toast } from "react-toastify";
import { FN } from "./facilityNetworkTheme";

type Props = {
  selected: ChildFacilitySearchHit[];
  onSelectedChange: (hits: ChildFacilitySearchHit[]) => void;
  onReview: () => void;
  onCancel: () => void;
  onOpenBulkAdd?: () => void;
  onFileUpload?: (file: File) => Promise<boolean> | boolean;
  reviewing: boolean;
  reviewDisabled?: boolean;
  debounceMs?: number;
  parentFacilityId?: number | null;
  localFacilities?: FacilityHierarchyInput[] | null;
  searchFacilities?: (query: string) => Promise<ChildFacilitySearchHit[]>;
};

const CHIP_HOVER = "#086e86";
const CANCEL_BORDER = "#d4d4d4";
const SEARCH_SHADOW = "0px 2px 2px rgba(194,194,194,0.25)";
const DROP_BG = "#f3fdfd";
const DROP_BORDER = "#3b9bb1";
const FILE_ROW_BORDER = "#c8c6c4";
const csvInputAccept = ".csv,text/csv,application/csv,text/comma-separated-values";

export default function AddChildFacilitySearchPanel({
  selected,
  onSelectedChange,
  onReview,
  onCancel,
  onFileUpload,
  reviewing,
  reviewDisabled,
  debounceMs = 300,
  parentFacilityId = null,
  localFacilities = null,
  searchFacilities,
}: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ChildFacilitySearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bulkExpanded, setBulkExpanded] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      setDropdownOpen(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    setDropdownOpen(true);

    const executeSearch = (q: string) =>
      searchFacilities
        ? searchFacilities(q)
        : searchChildFacilities(q, undefined, {
            parentFacilityId,
            localFacilities,
          });

    const runSearchTimer = () => {
      executeSearch(trimmed)
        .then((hits) => {
          if (!cancelled) setResults(hits);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    };

    if (debounceMs <= 0) {
      runSearchTimer();
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(runSearchTimer, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, debounceMs, searchFacilities, parentFacilityId, localFacilities]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!searchWrapRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const addHit = (hit: ChildFacilitySearchHit) => {
    onSelectedChange(mergeSelectedChildFacilities(selected, [hit]));
    setQuery("");
    setResults([]);
    setDropdownOpen(false);
  };

  const removeHit = (npi: string) => {
    onSelectedChange(selected.filter((hit) => hit.npi !== npi));
  };

  const handleApplyPaste = () => {
    const parsed = parsePastedCcns(pasteText);
    if (parsed.length === 0) {
      toast.info("No valid CCNs or NPIs found in pasted text.");
      return;
    }
    const newHits = parsed.map(mapNotFoundNpi);
    onSelectedChange(mergeSelectedChildFacilities(selected, newHits));
    setPasteText("");
    toast.success(`Added ${parsed.length} facilities to chip tray.`);
  };

  const handleApplyFile = async (uploadedFile: File | undefined) => {
    if (!uploadedFile) return;
    if (!isCsvUploadFile(uploadedFile)) {
      toast.error("Upload a CSV file.");
      return;
    }
    setFile(uploadedFile);
    if (onFileUpload) {
      await onFileUpload(uploadedFile);
    }
  };

  const count = selected.length;
  const reviewLabel = count === 1 ? "Review 1 facility" : `Review ${count} facilities`;
  const showDropdown = dropdownOpen && query.trim().length >= 2;

  return (
    <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      {reviewing ? (
        <Box
          data-testid="add-child-processing-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Processing upload"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            bgcolor: "rgba(255,255,255,0.78)",
            borderRadius: "8px",
            minHeight: 160,
          }}
        >
          <CircularProgress size={28} sx={{ color: FN.primary }} />
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: FN.figmaNeutral,
              lineHeight: "20px",
            }}
          >
            Processing upload…
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.figmaAccent,
              lineHeight: "16px",
            }}
          >
            Looking up facilities. Review screen opens next.
          </Typography>
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          width: "100%",
          pointerEvents: reviewing ? "none" : "auto",
          opacity: reviewing ? 0.55 : 1,
        }}
      >
        <Box ref={searchWrapRef} sx={{ position: "relative", width: "100%" }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            disabled={reviewing}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => {
              if (query.trim().length >= 2) setDropdownOpen(true);
            }}
            placeholder="Search by CCN, NPI, or facility name..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FacilityNetworkFigmaIcon src={searchIcon} size={16} />
                </InputAdornment>
              ),
              endAdornment: searching ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} sx={{ color: FN.primary }} />
                </InputAdornment>
              ) : undefined,
            }}
            inputProps={{ "aria-label": "Search child facilities" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "8px",
                bgcolor: "#fff",
                fontFamily: FN.fontFamily,
                fontSize: 14,
                boxShadow: SEARCH_SHADOW,
                "& fieldset": { borderColor: FN.figmaCardBorder },
                "&:hover fieldset": { borderColor: FN.figmaCardBorder },
                "&.Mui-focused fieldset": { borderColor: FN.primary },
              },
              "& input::placeholder": {
                color: FN.muted,
                opacity: 1,
              },
            }}
          />
          {showDropdown ? (
            <Box
              role="listbox"
              aria-label="Facility search results"
              sx={{
                position: "absolute",
                zIndex: 2,
                left: 0,
                right: 0,
                top: "calc(100% + 2px)",
                maxHeight: 162,
                overflowY: "auto",
                bgcolor: "#fff",
                border: `1px solid ${FN.figmaCardBorder}`,
                borderRadius: "8px",
                boxShadow: "0px 2px 4px rgba(194,194,194,0.25)",
              }}
            >
              {!searching && results.length === 0 ? (
                <Typography sx={{ px: 2, py: 1.5, fontSize: 12, color: FN.figmaAccent }}>
                  No facilities found
                </Typography>
              ) : (
                results.map((hit) => (
                  <Box
                    key={hit.npi}
                    component="button"
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => addHit(hit)}
                    sx={{
                      display: "flex",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderBottom: `1px solid ${FN.figmaCardBorder}`,
                      bgcolor: "#fff",
                      cursor: "pointer",
                      px: 1.5,
                      py: 1,
                      "&:last-of-type": { borderBottom: "none" },
                      "&:hover": { bgcolor: FN.listItemBg },
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: FN.fontFamily,
                          fontSize: 12,
                          fontWeight: 600,
                          color: FN.figmaNeutral,
                          lineHeight: "16px",
                        }}
                      >
                        {hit.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FN.fontFamily,
                          fontSize: 12,
                          color: FN.figmaAccent,
                          lineHeight: "16px",
                        }}
                      >
                        {formatSearchSubtitle(hit)}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.figmaAccent,
              letterSpacing: "0.03px",
              lineHeight: "16px",
            }}
          >
            Adding many at once?
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => setBulkExpanded(!bulkExpanded)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              px: 1.5,
              py: 0.75,
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.primary,
              lineHeight: "16px",
              letterSpacing: "0.03px",
            }}
          >
            {bulkExpanded ? "Hide bulk input" : "Paste a list or upload a CSV"}
            <Box
              sx={{
                display: "inline-flex",
                transform: bulkExpanded ? "rotate(90deg)" : "none",
                transition: "transform 0.2s",
              }}
            >
              <FacilityNetworkFigmaIcon src={csvChevronIcon} size={12} />
            </Box>
          </Box>
        </Box>

        {bulkExpanded ? (
          <Box
            sx={{
              border: `1px solid ${FN.figmaCardBorder}`,
              borderRadius: "8px",
              bgcolor: "#fafafa",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  fontWeight: 600,
                  color: FN.figmaNeutral,
                }}
              >
                Paste list of CCNs (one per line)
              </Typography>
              <TextField
                multiline
                rows={3}
                placeholder="Paste CCNs here e.g. 500124&#10;675321"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleApplyPaste}
                  disabled={!pasteText.trim()}
                  sx={{
                    textTransform: "none",
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    borderColor: FN.primary,
                    color: FN.primary,
                  }}
                >
                  Add to review list
                </Button>
              </Box>
            </Box>

            <Box sx={{ borderTop: `1px solid ${FN.figmaCardBorder}`, pt: 1.5 }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  fontWeight: 600,
                  color: FN.figmaNeutral,
                  mb: 1,
                }}
              >
                Or upload CSV file
              </Typography>

              <input
                ref={fileInputRef}
                type="file"
                accept={csvInputAccept}
                style={{ display: "none" }}
                onChange={(e) => {
                  const uploaded = e.target.files?.[0];
                  e.target.value = "";
                  void handleApplyFile(uploaded);
                }}
              />

              {!file ? (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    void handleApplyFile(e.dataTransfer.files?.[0]);
                  }}
                  sx={{
                    border: `1px dashed ${dragging ? FN.primary : DROP_BORDER}`,
                    borderRadius: "4px",
                    bgcolor: DROP_BG,
                    p: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <img src={cloudUploadIcon} alt="" style={{ width: 28, height: 28 }} />
                  <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.text }}>
                    Drag and drop CSV here, or{" "}
                    <Box component="span" sx={{ color: FN.primary, textDecoration: "underline" }}>
                      browse
                    </Box>
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    border: `1px solid ${FILE_ROW_BORDER}`,
                    borderRadius: "4px",
                    bgcolor: "#fff",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {reviewing ? (
                      <CircularProgress size={18} sx={{ color: FN.primary }} />
                    ) : (
                      <FacilityNetworkFigmaIcon src={documentIcon} size={20} />
                    )}
                    <Box>
                      <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, fontWeight: 600 }}>
                        {file.name}
                      </Typography>
                      <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 10, color: FN.figmaAccent }}>
                        {reviewing ? "Processing…" : formatCsvFileSize(file.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    disabled={reviewing}
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setFile(null)}
                    sx={{
                      border: "none",
                      bgcolor: "transparent",
                      cursor: reviewing ? "default" : "pointer",
                      opacity: reviewing ? 0.4 : 1,
                    }}
                  >
                    <FacilityNetworkFigmaIcon src={fileDismissIcon} size={16} />
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => downloadNpiCsv(CHILD_NPI_CSV_TEMPLATE_FILENAME, ["CCN"], [])}
                  sx={{
                    border: "none",
                    bgcolor: "transparent",
                    cursor: "pointer",
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.primary,
                    textDecoration: "underline",
                  }}
                >
                  Download template CSV
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>

      {count > 0 ? (
        <Box
          sx={{
            bgcolor: "#fff",
            border: `1px solid ${FN.figmaCardBorder}`,
            borderRadius: "8px",
            boxShadow: SEARCH_SHADOW,
            p: "17px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 20,
                  fontWeight: 500,
                  color: FN.title,
                  lineHeight: "28px",
                }}
              >
                Ready to Review
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "24px",
                  bgcolor: FN.badgeBg,
                  boxShadow: "0px 2px 2px #e9f3f5",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 600,
                    color: FN.title,
                    lineHeight: "20px",
                  }}
                >
                  {count}
                </Typography>
              </Box>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => onSelectedChange([])}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                px: 1.5,
                py: 0.75,
                fontFamily: FN.fontFamily,
                fontSize: 12,
                color: FN.primary,
                lineHeight: "16px",
                letterSpacing: "0.03px",
              }}
            >
              Clear all
              <FacilityNetworkFigmaIcon src={clearIcon} size={12} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {selected.map((hit) => (
              <Box
                key={hit.npi}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: "9px",
                  py: "5px",
                  borderRadius: "68px",
                  bgcolor: FN.panelMutedBorder,
                  border: `1px solid ${FN.panelBorder}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    color: CHIP_HOVER,
                    lineHeight: "20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatReviewChipLabel(hit)}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  aria-label={`Remove ${hit.name}`}
                  onClick={() => removeHit(hit.npi)}
                  sx={{
                    border: "none",
                    bgcolor: "transparent",
                    cursor: "pointer",
                    p: 0,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FacilityNetworkFigmaIcon src={chipDismissIcon} size={20} />
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={onCancel}
              sx={{
                textTransform: "none",
                minHeight: 32,
                px: 1.5,
                py: 0.75,
                borderRadius: "4px",
                border: `1px solid ${CANCEL_BORDER}`,
                bgcolor: "#fff",
                color: FN.text,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                "&:hover": { bgcolor: "#fafafa", border: `1px solid ${CANCEL_BORDER}` },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={reviewDisabled || reviewing || count === 0}
              onClick={onReview}
              endIcon={
                reviewing ? (
                  <CircularProgress size={12} sx={{ color: "#fff" }} />
                ) : (
                  <FacilityNetworkFigmaIcon src={reviewChevronIcon} size={12} />
                )
              }
              sx={{
                textTransform: "none",
                minHeight: 32,
                px: 1.5,
                py: 0.75,
                borderRadius: "4px",
                bgcolor: FN.primary,
                color: "#fff",
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                boxShadow: "none",
                "& .MuiButton-endIcon": { ml: "4px", mr: 0 },
                "&:hover": { bgcolor: FN.pressed, boxShadow: "none" },
                "&.Mui-disabled": {
                  color: "#fff",
                  bgcolor: "#b8dde6",
                },
              }}
            >
              {reviewLabel}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
